import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/pro-light-svg-icons';
import { initializeRuntimeConfig, setRuntimeConfig } from '@utils/runtimeConfig';
export const RuntimeConfigContext = createContext({
    config: null,
    isInitialized: false,
});
/**
 * Hook to access runtime configuration
 * @returns RuntimeConfigContextValue
 */
export const useRuntimeConfig = () => {
    const context = useContext(RuntimeConfigContext);
    if (!context) {
        throw new Error('useRuntimeConfig must be used within RuntimeConfigProvider');
    }
    return context;
};
/**
 * Loading Component
 */
function LoadingComponent() {
    return (_jsx("div", { className: "flex h-screen w-screen items-center justify-center", "data-testid": "runtime-config-loading", children: _jsx(FontAwesomeIcon, { icon: faSpinner, spin: true, size: "2x" }) }));
}
/**
 * Runtime Configuration Provider Component
 *
 * Responsibilities:
 * 1. Fetches runtime configuration from svc_lookup and svc_config endpoints
 *
 * Shows loading screen while initializing
 *
 * Note: Firebase is initialized separately in the auth saga during auth flow
 */
export const RuntimeConfigProvider = ({ children }) => {
    const [initState, setInitState] = useState('initializing');
    const [config, setConfig] = useState(null);
    useEffect(() => {
        let isMounted = true;
        const initialize = async () => {
            console.log('[RuntimeConfigProvider] Initializing runtime configuration...');
            setInitState('initializing');
            // Step 1: Fetch runtime configuration
            // Note: initializeRuntimeConfig() already handles errors and returns fallback values
            const runtimeConfig = await initializeRuntimeConfig();
            if (!isMounted)
                return;
            // Step 2: Set module-level runtime config for utilities and sagas
            setRuntimeConfig(runtimeConfig);
            if (!isMounted)
                return;
            setConfig(runtimeConfig);
            setInitState('initialized');
        };
        initialize();
        return () => {
            isMounted = false;
        };
    }, []);
    // Memoize context value to prevent unnecessary re-renders
    const contextValue = useMemo(() => ({
        config,
        isInitialized: initState === 'initialized',
    }), [config, initState]);
    // Show loading screen while initializing
    if (initState === 'initializing' || !config) {
        return _jsx(LoadingComponent, {});
    }
    // Note: We always proceed with config (either fetched or fallback), never block the app
    return _jsx(RuntimeConfigContext.Provider, { value: contextValue, children: children });
};
export default RuntimeConfigProvider;
